import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import { trackPageView } from "@/lib/analytics";
import { OrganizationProvider } from "@/lib/contexts/OrganizationContext";
import { UserProvider } from "@/lib/contexts/UserContext";
import { AuthProvider } from "@/lib/auth/AuthContext";
import { ApiProvider } from "@/lib/contexts/ApiContext";
import { ThemeProvider } from "@/lib/contexts/ThemeContext";
import { useAuth } from "@/lib/auth/useAuth";
import * as Sentry from "@sentry/react";

// Critical pages loaded immediately
import { AuthPage } from "@/pages/AuthPage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import DashboardLayout from "./components/layout/DashboardLayout";

// Lazy-loaded pages for code splitting
const Runs = lazy(() => import("./pages/Runs"));
const RunDetails = lazy(() => import("./pages/RunDetails"));
const Alerts = lazy(() => import("./pages/Alerts"));
const AlertDetails = lazy(() => import("./pages/AlertDetails"));
const Integrations = lazy(() => import("./pages/Integrations"));
const IntegrationDetails = lazy(() => import("./pages/IntegrationDetails"));
const GitHubCallback = lazy(() => import("./pages/GitHubCallback"));
const CircleCICallback = lazy(() => import("./pages/CircleCICallback"));
const SlackCallback = lazy(() => import("./pages/SlackCallback"));
const TeamsCallback = lazy(() => import("./pages/TeamsCallback"));
const Settings = lazy(() => import("./pages/Settings"));
const Usage = lazy(() => import("./pages/Usage"));
const TeamMemberPage = lazy(() => import("./pages/TeamMemberPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const WebhookManagement = lazy(() => import('./pages/WebhookManagement'));
const GitLabCallback = lazy(() => import("./pages/GitLabCallback"));
const InvitationAccept = lazy(() => import("./pages/InvitationAccept"));

const queryClient = new QueryClient();

// Loading fallback component for lazy-loaded routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
  </div>
);

// Component to handle page view tracking
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, {
      path: location.pathname,
      search: location.search,
      hash: location.hash
    });
  }, [location]);

  return null;
};

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // If not authenticated and not already on auth page, redirect to auth
  if (!isAuthenticated && !location.pathname.startsWith('/auth')) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

const AppContent = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <AuthProvider>
          <ApiProvider>
            <UserProvider>
              <OrganizationProvider>
                <Toaster />
                <Sonner />
                <PageViewTracker />
              <Routes>
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/auth/callback" element={
                  <ProtectedRoute>
                    <AuthPage />
                  </ProtectedRoute>
                } />
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                } />
                <Route path="/invitations/:token" element={
                  <Suspense fallback={<PageLoader />}>
                    <InvitationAccept />
                  </Suspense>
                } />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                } />
                <Route path="/runs" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Runs />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/runs/:runId" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <RunDetails />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/alerts" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Alerts />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/alerts/:alertId" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <AlertDetails />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/team-member/:memberId" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <TeamMemberPage />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Integrations />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/:providerId" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <IntegrationDetails />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/github/callback" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <GitHubCallback />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/gitlab/callback" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <GitLabCallback />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/circleci/callback" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <CircleCICallback />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/slack/callback" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <SlackCallback />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/teams/callback" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <TeamsCallback />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/integrations/webhooks" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <WebhookManagement />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Settings />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/usage" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <Usage />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Suspense fallback={<PageLoader />}>
                        <ProfilePage />
                      </Suspense>
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </OrganizationProvider>
          </UserProvider>
        </ApiProvider>
      </AuthProvider>
    </TooltipProvider>
  </ThemeProvider>
  </QueryClientProvider>
);

const App = () => (
  <Sentry.ErrorBoundary 
    fallback={({ error, resetError }) => (
      <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="text-xl font-bold text-red-600 mb-4">Application Error</h1>
          <p className="text-gray-700 mb-4">
            The application encountered an unexpected error. This has been automatically reported.
          </p>
          <details className="mb-4">
            <summary className="cursor-pointer text-sm text-gray-600">Technical Details</summary>
            <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-32">
              {error.message}
            </pre>
          </details>
          <div className="flex space-x-2">
            <button 
              onClick={resetError}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    )}
    beforeCapture={(scope, error, errorInfo) => {
      scope.setTag('error_boundary', 'app_level');
      scope.setContext('error_info', errorInfo);
      scope.setContext('app_state', {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        localStorage_keys: Object.keys(localStorage)
      });
    }}
  >
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  </Sentry.ErrorBoundary>
);

export default App;
